import type { FC } from 'react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { AIFormState } from '@trinity/chat';
import type { apiSchemas } from '@trinity/management-app-client';
import { DynamicFormWizard } from '../DynamicForm.wizard';
import { FormDisclaimerModal } from './FormDisclaimerModal';
import { syncDynamicFormToIConnect } from './sync';
import type { FormWorkflowIntroConfig } from './types';

type FormSchema = apiSchemas.FormSchema;
type Step = 'initial' | 'manual-entry' | 'submitted' | 'cancelled';

interface IconnectAwareFormWorkflowProps {
  queryId: number;
  queryIdentifier?: string;
  workflowType: string;
  value: Record<string, unknown> & {
    workflowType?: string;
    formState?: Step;
    showButtons?: boolean;
    isFromSpeech?: boolean;
    formConfig?: FormSchema;
    aiState?: AIFormState;
    submittedData?: {
      formData?: Array<{
        fieldLabel: string;
        fieldType: string;
        value: unknown;
      }>;
    };
    submittedAt?: string | number | Date;
    introConfig?: FormWorkflowIntroConfig;
  };
  introConfig: FormWorkflowIntroConfig;
}

export const IconnectAwareFormWorkflow: FC<IconnectAwareFormWorkflowProps> = memo((props) => {
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const pendingSubmitWithSyncRef = useRef<null | (() => Promise<void>)>(null);
  const pendingSubmitOnlyRef = useRef<null | (() => Promise<void>)>(null);
  const formConfig = props.value.formConfig;
  const iconnectSyncConfig = props.introConfig.iconnectSync;

  const shouldInterceptSubmit = !!iconnectSyncConfig?.enabled && !!iconnectSyncConfig?.mapping && !!formConfig;
  const openDisclaimer = useCallback(() => {
    setIsDisclaimerOpen(true);
  }, []);

  const closeDisclaimer = useCallback(() => {
    setIsDisclaimerOpen(false);
    pendingSubmitWithSyncRef.current = null;
    pendingSubmitOnlyRef.current = null;
  }, []);

  const submitBehavior = useMemo(() => {
    if (!shouldInterceptSubmit || !formConfig) {
      return undefined;
    }

    return {
      onBeforeSubmit: async (dynamicFormData: Record<string, unknown>) => {
        await syncDynamicFormToIConnect({
          formConfig,
          dynamicFormData,
          introConfig: props.introConfig,
        });
      },
      onRequestSubmit: async (controls: { submitWithPreSubmit: () => Promise<void>; submitOnly: () => Promise<void> }) => {
        pendingSubmitWithSyncRef.current = controls.submitWithPreSubmit;
        pendingSubmitOnlyRef.current = controls.submitOnly;
        openDisclaimer();
      },
    };
  }, [formConfig, openDisclaimer, props.introConfig, shouldInterceptSubmit]);

  return (
    <>
      <DynamicFormWizard
        queryId={props.queryId}
        queryIdentifier={props.queryIdentifier}
        workflowType={props.workflowType}
        value={props.value}
        introConfig={props.introConfig}
        submitBehavior={submitBehavior}
      />
      {shouldInterceptSubmit && (
        <FormDisclaimerModal
          open={isDisclaimerOpen}
          onClose={closeDisclaimer}
          onSyncToIConnect={async () => {
            const submit = pendingSubmitWithSyncRef.current;
            closeDisclaimer();
            if (submit) {
              await submit();
            }
          }}
          onSubmit={async () => {
            const submit = pendingSubmitOnlyRef.current;
            closeDisclaimer();
            if (submit) {
              await submit();
            }
          }}
          title={iconnectSyncConfig?.disclaimerTitle}
          disclaimerText={iconnectSyncConfig?.disclaimerText}
        />
      )}
    </>
  );
});

export default IconnectAwareFormWorkflow;
